export const CHAT_API = import.meta.env.VITE_CHAT_API_URL || ''

let _loggingOut = false

function getToken() {
  return localStorage.getItem('chat_session_token')
}

function clearAllChatStorage() {
  // Remove auth tokens
  localStorage.removeItem('chat_session_token')
  localStorage.removeItem('chat_user')
  localStorage.removeItem('chat_last_read')
  sessionStorage.removeItem('paradigm_active_channel')
  // Remove all paradigm_cache_* entries
  const toRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('paradigm_cache_')) toRemove.push(key)
  }
  toRemove.forEach(k => localStorage.removeItem(k))
}

// Explicit sign-out. `ecosystem = true` logs out of every ofself app. Relays to
// the backend (which tells Paradigm to revoke the session), then clears local
// state and reloads so the LoginGate takes over. Best-effort: local state is
// cleared even if the relay fails.
export async function logout(ecosystem = false) {
  try {
    await apiFetch(ecosystem ? '/api/chat/auth/logout-ecosystem' : '/api/chat/auth/logout', { method: 'POST' })
  } catch (e) { /* clear locally regardless */ }
  clearAllChatStorage()
  window.location.assign('/')
}

// Raw authed transport — returns the Response untouched. For consumers that
// need fetch semantics with the Bearer token carried (the shell's <Nucleus>
// takes this as its `fetcher`); apiFetch below stays the JSON-path door.
export function authedFetch(url, options = {}) {
  const token = getToken()
  return fetch(url, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })
}

async function apiFetch(path, options = {}) {
  const token = getToken()
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  }

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(`${CHAT_API}${path}`, {
    ...options,
    headers,
  })

  if (res.status === 401) {
    // Guard: only the first 401 triggers the redirect to prevent reload loops
    if (!_loggingOut) {
      _loggingOut = true
      clearAllChatStorage()
      window.location.href = '/'
    }
    throw new Error('Unauthorized')
  }

  const data = await res.json()
  if (!res.ok) {
    throw new Error(data.error?.message || 'API error')
  }
  return data
}

// Auth — exchange SDK callback params for a chat session token
export const exchangeCallback = (user_id, client_id, username, sid_code) =>
  apiFetch('/api/chat/auth/callback', {
    method: 'POST',
    body: JSON.stringify({ user_id, client_id, username, sid_code })
  })

// Auth
export const getMe = () => apiFetch('/api/chat/me')

// Exposure-profile status (EP expiry + per-group app authorization) for the
// rail identity switcher.
export const getEpStatus = () => apiFetch('/api/chat/auth/ep-status')

// User search (proxied through chat backend to SDK)
export const searchUsers = (query) =>
  apiFetch(`/api/chat/users/search?q=${encodeURIComponent(query)}`)

// Friends (from Paradigm SDK)
export const listFriends = () => apiFetch('/api/chat/friends')

// Channels (flat — no workspace)
export const listChannels = () => apiFetch('/api/chat/channels')
export const createChannel = (body) =>
  apiFetch('/api/chat/channels', { method: 'POST', body: JSON.stringify(body) })
export const joinChannel = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/members`, { method: 'POST', body: JSON.stringify({}) })
export const listChannelMembers = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/members`)
export const addChannelMember = (channelId, userId, username) =>
  apiFetch(`/api/chat/channels/${channelId}/members`, { method: 'POST', body: JSON.stringify({ user_id: userId, username }) })
export const removeChannelMember = (channelId, userId) =>
  apiFetch(`/api/chat/channels/${channelId}/members/${userId}`, { method: 'DELETE' })
export const leaveChannel = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/members`, { method: 'DELETE' })
export const deleteChannel = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}`, { method: 'DELETE' })
export const renameChannel = (channelId, name) =>
  apiFetch(`/api/chat/channels/${channelId}`, { method: 'PUT', body: JSON.stringify({ name }) })
// The channel's property ledger (the shell's <Properties> state, verbatim).
// Whole-ledger replacement: the client's arrays ARE the order, so there is
// nothing for a patch language to express that this doesn't.
export const setChannelProperties = (channelId, properties, hidden) =>
  apiFetch(`/api/chat/channels/${channelId}/properties`, { method: 'PUT', body: JSON.stringify({ properties, hidden }) })

// WORLDS — a group's atrium rooms (the group's `workspace` nodes), and which a channel stands in
export const listWorlds = (groupId) => apiFetch(`/api/chat/groups/${groupId}/worlds`)
export const createWorld = (groupId, name) =>
  apiFetch(`/api/chat/groups/${groupId}/worlds`, { method: 'POST', body: JSON.stringify({ name }) })
/** A world's fields: { name } renames it, { order } ranks it (null: unranked). */
export const updateWorld = (groupId, worldId, fields) =>
  apiFetch(`/api/chat/groups/${groupId}/worlds/${worldId}`, { method: 'PUT', body: JSON.stringify(fields) })
export const deleteWorld = (groupId, worldId) =>
  apiFetch(`/api/chat/groups/${groupId}/worlds/${worldId}`, { method: 'DELETE' })
/** A channel's worlds changed by an op, { add, remove }, on what it holds now → { channel, before }. */
export const moveChannelWorlds = (channelId, { add = [], remove = [] }) =>
  apiFetch(`/api/chat/channels/${channelId}/worlds`, { method: 'PUT', body: JSON.stringify({ add, remove }) })

// Channel mute
export const toggleChannelMute = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/mute`, { method: 'PUT', body: JSON.stringify({}) })
export const getMutedChannels = () =>
  apiFetch('/api/chat/channels/muted')

// Pinned conversations
export const toggleChannelPin = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/pin`, { method: 'PUT' })
export const getPinnedChannels = () => apiFetch('/api/chat/channels/pinned')

// Pinned messages
export const pinMessage = (channelId, messageId) =>
  apiFetch(`/api/chat/channels/${channelId}/pins`, { method: 'POST', body: JSON.stringify({ message_id: messageId }) })
export const unpinMessage = (channelId, messageId) =>
  apiFetch(`/api/chat/channels/${channelId}/pins/${messageId}`, { method: 'DELETE' })
export const getPinnedMessages = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/pins`)

// Link preview
export const fetchLinkPreview = (url) =>
  apiFetch(`/api/chat/link-preview?url=${encodeURIComponent(url)}`)

// Read receipts
export const markChannelRead = (channelId, messageId) =>
  apiFetch(`/api/chat/channels/${channelId}/read`, { method: 'POST', body: JSON.stringify({ message_id: messageId }) })
export const getReadStatus = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/read-status`)

// Messages
export const listMessages = (channelId, params = {}) => {
  const qs = new URLSearchParams(params).toString()
  return apiFetch(`/api/chat/channels/${channelId}/messages${qs ? '?' + qs : ''}`)
}
export const sendMessage = (channelId, body) =>
  apiFetch(`/api/chat/channels/${channelId}/messages`, { method: 'POST', body: JSON.stringify(body) })
export const searchMessages = (channelId, query) =>
  apiFetch(`/api/chat/channels/${channelId}/messages/search?q=${encodeURIComponent(query)}`)
// EVERY channel you are in, one query. The scoped search above is the same
// question narrowed; which one runs is the only difference between "search
// this conversation" and "search synapse".
export const searchMessagesGlobal = (query) =>
  apiFetch(`/api/chat/messages/search?q=${encodeURIComponent(query)}`)
export const listThreadReplies = (messageId) =>
  apiFetch(`/api/chat/messages/${messageId}/replies`)
export const sendThreadReply = (channelId, threadId, content, fileId) => {
  const body = { content, thread_id: threadId }
  if (fileId) body.file_id = fileId
  return apiFetch(`/api/chat/channels/${channelId}/messages`, {
    method: 'POST', body: JSON.stringify(body)
  })
}
export const editMessage = (messageId, content) =>
  apiFetch(`/api/chat/messages/${messageId}`, { method: 'PUT', body: JSON.stringify({ content }) })
export const deleteMessage = (messageId) =>
  apiFetch(`/api/chat/messages/${messageId}`, { method: 'DELETE' })
export const addReaction = (messageId, emoji) =>
  apiFetch(`/api/chat/messages/${messageId}/reactions`, { method: 'POST', body: JSON.stringify({ emoji }) })
export const removeReaction = (messageId, emoji) =>
  apiFetch(`/api/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`, { method: 'DELETE' })

// Direct Messages (flat — no workspace)
export const listDMs = () => apiFetch('/api/chat/dms')
export const createDM = (userIds, usernames = {}) => {
  const body = Array.isArray(userIds)
    ? { user_ids: userIds, usernames }
    : { user_id: userIds, usernames }
  return apiFetch('/api/chat/dm', { method: 'POST', body: JSON.stringify(body) })
}
export const createPluginDM = (pluginId, pluginName, subEntityKey) =>
  apiFetch('/api/chat/dm/plugin', {
    method: 'POST',
    body: JSON.stringify({ plugin_id: pluginId, plugin_name: pluginName, sub_entity_key: subEntityKey }),
  })


// Files
export const uploadFile = (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return apiFetch('/api/chat/files', { method: 'POST', body: formData })
}

// VOICE (the conversation rail's seat, the kit's useDictation): the browser
// records, the server transcribes, the words come back for the composer.
export const transcribeDictation = async (audio, filename) => {
  const formData = new FormData()
  formData.append('audio', audio, filename)
  return (await apiFetch('/api/chat/transcribe', { method: 'POST', body: formData })).text || ''
}

// User avatars — image lives in SDK as a rawfile node;
// the chat backend owns the reference + a short version hash that
// cache-busts the <img> URL when the photo changes.
export const uploadAvatar = (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return apiFetch('/api/chat/users/me/avatar', { method: 'POST', body: formData })
}
export const deleteAvatar = () =>
  apiFetch('/api/chat/users/me/avatar', { method: 'DELETE' })
export const getUserProfile = (userId) =>
  apiFetch(`/api/chat/users/${encodeURIComponent(userId)}/profile`)
export const buildAvatarUrl = (relativePath) => {
  if (!relativePath) return null
  if (relativePath.startsWith('http')) return relativePath
  return `${CHAT_API}${relativePath}`
}
// `v` busts browser caches poisoned by a past bug that stamped a one-year
// immutable Cache-Control onto the S3 presigned *redirect* — those clients hold
// a cached redirect to a long-dead signature and would keep showing a broken
// image until the entry aged out. Bump it if that ever happens again.
const FILE_URL_VERSION = '2'
export const getFileUrl = (fileId) => {
  const token = getToken()
  const params = new URLSearchParams({ v: FILE_URL_VERSION })
  if (token) params.set('token', token)
  return `${CHAT_API}/api/chat/files/${fileId}?${params}`
}
export const listFiles = () => apiFetch('/api/chat/files')
export const getUnread = () => apiFetch('/api/chat/unread')
export const markChannelUnread = (channelId, messageId) =>
  apiFetch(`/api/chat/channels/${channelId}/mark-unread`, { method: 'POST', body: JSON.stringify({ message_id: messageId }) })
export const getActivity = () => apiFetch('/api/chat/activity')

// Members (all users who share channels with current user)
export const listMembers = () => apiFetch('/api/chat/members')

// Groups (Paradigm SDK)
export const listGroups = () => apiFetch('/api/chat/groups')
export const getGroupChannel = (groupId) => apiFetch(`/api/chat/groups/${groupId}/channel`)
export const listGroupMembers = (groupId) => apiFetch(`/api/chat/groups/${groupId}/members`)
export const listGroupChannels = (groupId) => apiFetch(`/api/chat/groups/${groupId}/channels`)
export const createGroupChannel = (groupId, body) =>
  apiFetch(`/api/chat/groups/${groupId}/channels`, { method: 'POST', body: JSON.stringify(body) })

// Huddles
export const startHuddle = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/huddle`, { method: 'POST', body: JSON.stringify({}) })
export const leaveHuddle = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/huddle`, { method: 'DELETE' })
export const getHuddle = (channelId) =>
  apiFetch(`/api/chat/channels/${channelId}/huddle`)
export const toggleHuddleMute = (huddleId) =>
  apiFetch(`/api/chat/huddles/${huddleId}/mute`, { method: 'PATCH', body: JSON.stringify({}) })
export const toggleRaiseHand = (huddleId) =>
  apiFetch(`/api/chat/huddles/${huddleId}/raise-hand`, { method: 'PATCH', body: JSON.stringify({}) })
export const sendSignal = (huddleId, toUserId, msgType, payload) =>
  apiFetch(`/api/chat/huddles/${huddleId}/signal`, {
    method: 'POST', body: JSON.stringify({ to_user_id: toUserId, msg_type: msgType, payload })
  })
export const pollSignals = (huddleId) =>
  apiFetch(`/api/chat/huddles/${huddleId}/signal`)
export const getLivekitToken = (huddleId) =>
  apiFetch(`/api/chat/huddles/${huddleId}/token`, { method: 'POST', body: JSON.stringify({}) })
export const getActiveHuddles = () =>
  apiFetch('/api/chat/huddles/active')
export const getCallHistory = (limit = 30) =>
  apiFetch(`/api/chat/huddles/history?limit=${limit}`)
export const getHuddleRecordings = (huddleId) =>
  apiFetch(`/api/chat/huddles/${huddleId}/recordings`)

// Per-track huddle recordings.
// trackRecordings: [{ userId, username, trackSid, sessionIndex, blob, startedAt, endedAt }]
// Each entry becomes a rawfile on the backend, listed in recorded_as on the huddle's
// call. DM channels → each participant's own call. Group channels → the group's call.
export const getLiveGraph = (huddleId) =>
  apiFetch(`/api/chat/huddles/${huddleId}/live-graph`)

export const getGraphNodeSources = (huddleId, nodeId) =>
  apiFetch(`/api/chat/huddles/${huddleId}/graph-node/${nodeId}`)

export const postTranscriptChunk = (huddleId, audioBlob) => {
  const form = new FormData()
  form.append('audio', audioBlob, 'chunk.webm')
  return apiFetch(`/api/chat/huddles/${huddleId}/transcript-chunk`, {
    method: 'POST',
    body: form,
  })
}

export const uploadTrackRecordings = (channelId, huddleId, trackRecordings) => {
  const form = new FormData()
  if (huddleId) form.append('huddle_id', huddleId)
  // Metadata array (no blobs) so the backend knows who each file belongs to
  const meta = trackRecordings.map(({ blob: _b, ...rest }) => rest)
  form.append('tracks', JSON.stringify(meta))
  // One audio file per track, index-aligned with the metadata array
  trackRecordings.forEach((t, i) => {
    form.append(`track_audio_${i}`, t.blob, `track-${t.userId.slice(0, 8)}-s${t.sessionIndex}.webm`)
  })
  return apiFetch(`/api/chat/channels/${channelId}/videocall-messages`, {
    method: 'POST',
    body: form,
  })
}
